import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ButtonControl,
  IconControl,
  Loading,
  SidePanelFormControl,
  TableControl,
} from 'spark-core-dx/components';
import { useCorePermissions, useGenericManager } from 'spark-core-dx/hooks';
import AIPanel from '../../components/AIChat/AIPanel';
import ConvokeCommandBar from '../../components/convoke/ConvokeCommandBar';
import DialogControl from '../../components/convoke/ConvokeDialogControl';
import ConvokeDialogMessage from '../../components/convoke/ConvokeDialogMessage';
import CardGridControl from '../resource/components/CardGridControl';
import ResourceStateControl from '../resource/components/ResourceStateControl';
import ClientCommandStatusTag from './ClientCommandStatusTag';
import ClientInternalNotificationWizard from './ClientInternalNotificationWizard';
import {
  getFirstMetadata,
  getFirstResultItem,
  getResultItems,
  getResultMessage,
  isSuccessfulResult,
} from './clientEntryContracts';
import {
  CLIENT_COMMUNICATION_CONFIGURATION_FIELDS,
  CLIENT_COMMUNICATION_ITEM_NAMES,
  CLIENT_COMMUNICATION_OPERATIONS,
  CLIENT_COMMUNICATION_PROVIDER_CARD_METADATA,
  CLIENT_COMMUNICATION_TEST_FIELDS,
  EMPTY_NOTIFICATION_FILTER,
  EMPTY_NOTIFICATION_LOG_FILTER,
  INTERNAL_NOTIFICATION_ITEM_FIELD_NAMES,
  buildSafeCommunicationAIPayload,
  buildSafeNotificationDraftAIPayload,
  buildSafeNotificationLogDiagnostics,
  cloneCommunicationValue,
  communicationRecordId,
  communicationRecordVersion,
  createCommunicationRequest,
  createInternalNotificationDraft,
  normalizeNotificationAIDraft,
  normalizeNotificationFilter,
  normalizeNotificationLogFilter,
  notificationFilterChips,
  notificationLogFilterChips,
  resolveNotificationRecipientEmails,
  selectCommunicationFields,
} from './clientCommunicationContracts';
import { CLIENT_COMMAND_OPERATION_PERMISSIONS } from './clientCommandPermissions';

const SERVER_OWNED_FORM_FIELDS = new Set([
  'clientid',
  'appid',
  'createddate',
  'createdby',
  'createdbyid',
  'updateddate',
  'updatedby',
  'updatedbyid',
  'isdeleted',
  'isnew',
  'uniqueid',
  '_id',
]);

const NOTIFICATION_REQUEST_DEFAULT_GRID_FIELDS = new Map([
  ['title', 1],
  ['notificationtype', 2],
  ['deliverystatus', 3],
  ['startdate', 4],
  ['enddate', 5],
]);

const NOTIFICATION_REQUEST_HEADER_FEATURES = [
  'search',
  'filter',
  'tableStyles',
  'columnSettings',
  'operations',
  'viewAs',
];

const COMMUNICATION_AI_REFERENCE_NAME = 'ClientCommunicationManage';
const NOTIFICATION_REQUEST_AI_REFERENCE_NAME = 'NotificationRequest';
const NOTIFICATION_DRAFT_AI_REFERENCE_NAME = 'NotificationRequestDraft';

const applyNotificationAIRecipientSelection = (draftUpdate, recipients) => {
  if (!Object.prototype.hasOwnProperty.call(draftUpdate || {}, 'RecipientEmails')) {
    return draftUpdate;
  }
  const { RecipientEmails, ...safeDraftUpdate } = draftUpdate;
  const resolution = resolveNotificationRecipientEmails(RecipientEmails, recipients);
  if (resolution.UnmatchedRecipientEmails.length) {
    throw new Error(
      `The following email address${resolution.UnmatchedRecipientEmails.length === 1 ? ' is' : 'es are'} not an eligible active user for this client application: ${resolution.UnmatchedRecipientEmails.join(', ')}.`,
    );
  }
  return {
    ...safeDraftUpdate,
    RecipientProfileIds: resolution.RecipientProfileIds,
  };
};
const NEW_NOTIFICATION_AI_DRAFT_TITLE = 'New notification';
const NEW_NOTIFICATION_AI_DRAFT_MESSAGE = '<p>Draft message.</p>';

const notificationStreamPayload = (message) => {
  if (message?.Message && typeof message.Message === 'object') {
    return message.Message;
  }
  if (typeof message?.Message !== 'string') return {};
  try {
    return JSON.parse(message.Message);
  } catch {
    return { Message: message.Message };
  }
};

const mergeNotificationStreamMessage = (current, message, requestId, totalCount) => {
  const payload = notificationStreamPayload(message);
  const isComplete = Boolean(message?.IsComplete);
  const messageType = String(payload.MessageType || message?.UIMessageType || '').toLowerCase();
  const isPartial = isComplete && messageType.includes('warning');
  const isSuccessful = isComplete && payload.Success === true;
  const isFailed = isComplete && !isSuccessful && !isPartial;
  const statusLogId = payload.StatusLogId || '';
  const incomingLog = payload.Message ? {
    Id: statusLogId,
    CreatedDate: payload.CreatedDate || new Date().toISOString(),
    Message: payload.Message,
    MessageType: payload.MessageType || message?.UIMessageType || 'status',
    Processed: payload.Processed,
    Total: payload.Total,
  } : null;
  const logs = [...(current?.Logs || [])];
  if (incomingLog && !logs.some((entry) => (
    statusLogId
      ? entry.Id === statusLogId
      : entry.CreatedDate === incomingLog.CreatedDate && entry.Message === incomingLog.Message
  ))) {
    logs.push(incomingLog);
    logs.sort((left, right) => (
      new Date(left.CreatedDate || 0).getTime() - new Date(right.CreatedDate || 0).getTime()
    ));
  }
  const eventStatusLabel = isPartial
    ? 'Partial failure'
    : isSuccessful
      ? 'Completed'
      : isFailed
        ? 'Failed'
        : 'In Progress';
  const eventId = message?.EventId || current?.EventId || null;
  const eventHistory = (current?.EventHistory || []).map((entry) => (
    String(entry.EventId || '') === String(eventId || '')
      ? {
        ...entry,
        EventStatusLabel: eventStatusLabel,
        IsTerminal: isComplete,
      }
      : entry
  ));
  return {
    ...(current || {}),
    RequestId: current?.RequestId || requestId,
    EventId: eventId,
    EventStatusLabel: eventStatusLabel,
    IsTerminal: isComplete,
    IsSuccessful: isSuccessful,
    IsRetryable: isFailed,
    EventHistory: eventHistory,
    ProcessedCount: Number(payload.Processed ?? current?.ProcessedCount ?? 0),
    TotalCount: Number(payload.Total ?? current?.TotalCount ?? totalCount ?? 0),
    Logs: logs,
    StreamWarning: '',
  };
};

// Keep the correctly timed metadata state available for the later formatting pass.
const SHOW_INLINE_METADATA_LOADING = false;

const runOperation = async (
  manager,
  operationName,
  item,
  fallbackMessage,
) => {
  if (!manager?.RunOperation) throw new Error(fallbackMessage);
  const result = await manager.RunOperation({
    operationName,
    item,
    isItem: true,
    isWarning: true,
    supressAllErrors: false,
  });
  if (!isSuccessfulResult(result)) {
    throw new Error(getResultMessage(result, fallbackMessage));
  }
  return getFirstResultItem(result);
};

const withOperationTimeout = (operation, message, timeoutMs = 30000) => {
  let timer;
  return Promise.race([
    operation,
    new Promise((resolve, reject) => {
      timer = globalThis.setTimeout(() => reject(new Error(message)), timeoutMs);
    }),
  ]).finally(() => globalThis.clearTimeout(timer));
};

const CommunicationsState = ({
  loading,
  error,
  message,
}) => {
  if (loading) {
    return (
      <ResourceStateControl
        compact
        loading
        title="Loading Communications"
        loadingText="Loading client communication settings and tools."
      />
    );
  }
  if (error) {
    return (
      <ResourceStateControl
        compact
        tone="error"
        title="Communications unavailable"
        message={error}
      />
    );
  }
  return message ? (
    <ResourceStateControl
      compact
      title="Communications status"
      message={message}
      role="status"
    />
  ) : null;
};

const ProviderCards = ({
  providers,
  busy,
  canValidate,
  canTest,
  onValidate,
  onTest,
}) => (
  <section className="resource-workspace-panel" aria-labelledby="communication-providers">
    <div className="resource-workspace-section-title">
      <div>
        <h2 id="communication-providers">Channels and providers</h2>
        <p>
          Select Providers controls which communication channels are available.
          Configure Providers controls how selected external providers operate.
        </p>
      </div>
    </div>
    <div className="resource-workspace-record-list">
      {(providers || []).map((provider) => (
        <article
          className="resource-workspace-record-row"
          key={provider.Key}
        >
          <div>
            <strong>{provider.DisplayName}</strong>
            <span>{provider.Channel} — {provider.Description}</span>
          </div>
          <div className="resource-workspace-record-row-actions resource-workspace-record-row-actions--status">
            <ClientCommandStatusTag status={provider.Status} />
            {provider.CanValidate && canValidate ? (
              <ButtonControl
                type="icon-small"
                onClick={() => onValidate(provider)}
                disabled={busy}
              >
                <IconControl
                  iconKey="checklist"
                  title={`Validate ${provider.DisplayName}`}
                />
              </ButtonControl>
            ) : null}
            {provider.CanTest && canTest ? (
              <ButtonControl
                type="standard-small"
                onClick={() => onTest(provider)}
                disabled={busy}
              >
                Test
              </ButtonControl>
            ) : null}
          </div>
        </article>
      ))}
    </div>
  </section>
);

const ToolCards = ({
  tools,
  providers,
  busy,
  canLoad,
  onOpen,
}) => {
  const selectedProvidersByChannel = new Map(
    (providers || [])
      .filter((provider) => provider.IsSelected)
      .map((provider) => [
        String(provider.Channel || '').trim().toLowerCase(),
        provider,
      ]),
  );
  const isProviderConfigured = (provider) => (
    provider?.IsConfigured == null
      ? String(provider?.Status || '').trim().toLowerCase() === 'ready'
      : provider.IsConfigured
  );
  const visibleTools = (tools || []).filter((tool) => {
    const provider = selectedProvidersByChannel.get(
      String(tool.Channel || '').trim().toLowerCase(),
    );
    return isProviderConfigured(provider);
  });

  return (
    <section
      className="resource-workspace-panel command-communications__tools"
      aria-labelledby="communication-tools"
    >
      <div className="resource-workspace-section-title">
        <div>
          <h2 id="communication-tools">Management tools</h2>
          <p>
            Each tool reuses its existing metadata and Default Dynamic Grid
            inside the selected client and application boundary.
          </p>
        </div>
      </div>
      <div className="resource-workspace-record-list">
        {visibleTools.map((tool) => {
          const status = tool.CanView
            ? (tool.IsReadOnly ? 'View only' : 'Available')
            : 'Access denied';

          return (
            <article
              className="resource-workspace-record-row"
              key={tool.Key}
            >
              <div>
                <strong>{tool.DisplayName}</strong>
                <span className="command-communications__tool-description">
                  {tool.Channel} — {tool.Description}
                </span>
              </div>
              <div className="resource-workspace-record-row-actions resource-workspace-record-row-actions--stacked-status">
                <ClientCommandStatusTag
                  status={status}
                />
                {canLoad ? (
                  <ButtonControl
                    type="icon-small"
                    onClick={() => onOpen(tool)}
                    disabled={busy || !tool.CanView}
                  >
                    <IconControl
                      iconKey={tool.IsReadOnly ? 'view' : 'settings'}
                      color={
                        !busy && tool.CanView
                          ? 'var(--primary-text)'
                          : undefined
                      }
                      title={`${tool.IsReadOnly ? 'View' : 'Configure'} ${tool.DisplayName}`}
                    />
                  </ButtonControl>
                ) : null}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
};

const CommunicationToolContent = ({
  workspace,
  tool,
  busy,
  onLoadPage,
  onSave,
  onRemove,
  onAddNotification,
  onManageNotification,
  onClearNotificationFilters,
  canSaveItem,
  canRemoveItem,
  canSaveNotificationDraft,
}) => {
  const itemManager = useGenericManager(
    tool?.ItemName || CLIENT_COMMUNICATION_ITEM_NAMES.workspace,
  );
  const managerRef = useRef(itemManager);
  const [metadata, setMetadata] = useState(null);
  const [metadataError, setMetadataError] = useState('');
  const [panelMode, setPanelMode] = useState('');
  const [draft, setDraft] = useState(null);
  managerRef.current = itemManager;

  useEffect(() => {
    let active = true;
    setMetadata(null);
    setMetadataError('');
    if (!tool?.ItemName) return () => { active = false; };
    managerRef.current.GetMetadata({ forceReload: true })
      .then((result) => {
        if (active) {
          setMetadata(getFirstMetadata(result, managerRef.current));
        }
      })
      .catch((ex) => {
        if (active) {
          setMetadataError(ex?.message || `${tool.DisplayName} metadata is unavailable.`);
        }
      });
    return () => { active = false; };
  }, [tool?.ItemName, tool?.DisplayName]);

  const formFields = useMemo(
    () => (metadata?.Fields || metadata?.fields || []).filter((field) => (
      !SERVER_OWNED_FORM_FIELDS.has(
        String(field?.FieldName || field?.fieldName || '').toLowerCase(),
      )
    )),
    [metadata],
  );
  const gridMetadata = useMemo(
    () => {
      if (!metadata) return null;
      const notificationRequestTool = tool?.Key === 'notification-requests';
      const fields = metadata.Fields || metadata.fields || [];

      return {
        ...metadata,
        Fields: notificationRequestTool
          ? fields.map((field) => {
            const fieldName = String(
              field?.FieldName || field?.fieldName || '',
            ).toLowerCase();
            const defaultSequence = NOTIFICATION_REQUEST_DEFAULT_GRID_FIELDS
              .get(fieldName);
            return {
              ...field,
              ShowOnGrid: defaultSequence != null,
              ...(defaultSequence != null
                ? { GridSequence: defaultSequence }
                : {}),
            };
          })
          : fields,
        DisplayName: '',
        Description: '',
        DisableCanClone: true,
        DisableCanCreate:
          notificationRequestTool
          || tool?.IsReadOnly
          || !tool?.CanCreate
          || !canSaveItem
          || metadata.DisableCanCreate,
        DisableCanEdit:
          tool?.IsReadOnly || !tool?.CanEdit || !canSaveItem || metadata.DisableCanEdit,
        DisableCanDelete:
          tool?.IsReadOnly || !tool?.CanDelete || !canRemoveItem || metadata.DisableCanDelete,
      };
    },
    [canRemoveItem, canSaveItem, metadata, tool],
  );

  const openRecord = (record, mode = 'view') => {
    setDraft(cloneCommunicationValue(record));
    setPanelMode(mode);
  };

  const saveRecord = async (updated) => {
    const saved = await onSave(
      updated,
      panelMode === 'create' ? null : draft,
    );
    setPanelMode('');
    setDraft(null);
    return {
      Success: true,
      Items: saved?.Records || [],
    };
  };

  const currentPage = Number(workspace?.PageNumber || 1);
  const notificationRequestTool = tool?.Key === 'notification-requests';
  const notificationLogTool = tool?.Key === 'notifications-log';
  const appliedFilterChips = notificationRequestTool
    ? notificationFilterChips(workspace?.NotificationFilter)
    : notificationLogTool
      ? notificationLogFilterChips(workspace?.NotificationLogFilter)
      : [];
  const showNotificationRequestFirstUse = notificationRequestTool
    && Boolean(gridMetadata)
    && !busy
    && !metadataError
    && Number(workspace?.TotalRecords || 0) === 0
    && (workspace?.Records || []).length === 0
    && !String(workspace?.SearchText || '').trim()
    && appliedFilterChips.length === 0;

  return (
    <section
      className="command-communications__tool-management"
      aria-label={`${tool?.DisplayName || 'Communication'} management`}
    >
      <div className="resource-workspace-section-title resource-workspace-section-title--with-action">
        <div>
          <h2>{tool?.DisplayName}</h2>
          <p>
            {workspace?.TotalRecords || 0} selected-client records
            {tool?.IsReadOnly ? ' · view only' : ''}
          </p>
        </div>
        <div className="resource-workspace-section-actions">
          {notificationRequestTool && tool?.CanCreate && canSaveNotificationDraft ? (
            <ButtonControl
              type="ok-small"
              onClick={onAddNotification}
              disabled={busy}
            >
              <IconControl iconKey="add" title="Add Notification" />
              Add Notification
            </ButtonControl>
          ) : null}
          <ButtonControl
            type="icon-small"
            onClick={() => onLoadPage(currentPage, workspace?.SearchText || '')}
            disabled={busy}
            aria-label={`Refresh ${tool?.DisplayName || 'communication records'}`}
          >
            <IconControl
              iconKey="refresh"
              title={`Refresh ${tool?.DisplayName || 'communication records'}`}
              width="1rem"
              height="1rem"
            />
          </ButtonControl>
        </div>
      </div>

      {appliedFilterChips.length ? (
        <div
          className="command-communications__filter-chips"
          aria-label={`Applied ${tool?.DisplayName || 'communication'} filters`}
        >
          {appliedFilterChips.map((chip) => <span key={chip}>{chip}</span>)}
          <ButtonControl
            type="standard-small"
            onClick={onClearNotificationFilters}
            disabled={busy}
          >
            Clear filters
          </ButtonControl>
        </div>
      ) : null}

      <ConvokeDialogMessage message={metadataError} tone="error" />
      {SHOW_INLINE_METADATA_LOADING && !metadata && !metadataError
        ? <Loading />
        : null}
      {showNotificationRequestFirstUse ? (
        <div className="command-communications__empty-state" role="status">
          <strong>No notifications yet</strong>
          <span>
            Click Add Notification above to create your first notification.
          </span>
        </div>
      ) : gridMetadata ? (
        <TableControl
          data={workspace?.Records || []}
          defaultNumRows={workspace?.PageSize || 50}
          disableTableTitle
          enableMultiSelect={false}
          filters={itemManager?.filters}
          loadingState={busy}
          metadata={[gridMetadata]}
          mgr={itemManager}
          headerFeatures={notificationRequestTool
            ? NOTIFICATION_REQUEST_HEADER_FEATURES
            : undefined}
          onCreate={
            !notificationRequestTool && !tool?.IsReadOnly && tool?.CanCreate && canSaveItem
              ? () => {
                setDraft(cloneCommunicationValue(workspace?.NewRecordTemplate || {}));
                setPanelMode('create');
              }
              : undefined
          }
          onDelete={
            !tool?.IsReadOnly && tool?.CanDelete && canRemoveItem
              ? onRemove
              : undefined
          }
          onEdit={(record) => {
            if (notificationRequestTool) onManageNotification(record);
            else openRecord(
              record,
              tool?.IsReadOnly || !tool?.CanEdit || !canSaveItem ? 'view' : 'edit',
            );
          }}
        />
      ) : null}

      {!notificationRequestTool ? <SidePanelFormControl
        open={!!panelMode}
        title={`${panelMode === 'create' ? 'Add' : panelMode === 'edit' ? 'Edit' : 'View'} ${tool?.DisplayName || 'Communication record'}`}
        metadata={metadata}
        formFields={formFields}
        itemData={draft}
        onSave={saveRecord}
        onCancel={() => setPanelMode('')}
        onUpdateSidePanel={() => {}}
        saveButtonText="Save"
        setOpenEditSidePanel={(open) => {
          if (!open) setPanelMode('');
        }}
        setItemData={setDraft}
        viewOnly={panelMode === 'view'}
      /> : null}

    </section>
  );
};

const ClientCommunicationsScreen = ({
  screen,
  clientId,
  appId,
  currentEnvironment,
}) => {
  const { canExecute } = useCorePermissions();
  const workspaceManager = useGenericManager(
    CLIENT_COMMUNICATION_ITEM_NAMES.workspace,
  );
  const configurationManager = useGenericManager(
    CLIENT_COMMUNICATION_ITEM_NAMES.configuration,
  );
  const providerActionManager = useGenericManager(
    CLIENT_COMMUNICATION_ITEM_NAMES.providerAction,
  );
  const notificationRequestManager = useGenericManager(
    CLIENT_COMMUNICATION_ITEM_NAMES.notificationRequest,
  );
  const aiChatSessionManager = useGenericManager('AIChatSession');
  const workspaceRef = useRef(workspaceManager);
  const configurationRef = useRef(configurationManager);
  const providerActionRef = useRef(providerActionManager);
  const notificationRequestRef = useRef(notificationRequestManager);
  const aiChatSessionRef = useRef(aiChatSessionManager);
  const notificationStreamRef = useRef(null);
  const preparedNotificationDraftRef = useRef(null);
  const aiPreparedNotificationRequestIdRef = useRef(null);
  const notificationDraftSourceAISessionIdRef = useRef(null);
  const [workspace, setWorkspace] = useState(null);
  const [configurationMetadata, setConfigurationMetadata] = useState(null);
  const [providerActionMetadata, setProviderActionMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [operationResult, setOperationResult] = useState(null);
  const [providerSelectionOpen, setProviderSelectionOpen] = useState(false);
  const [selectedProviderKeys, setSelectedProviderKeys] = useState([]);
  const [configurationOpen, setConfigurationOpen] = useState(false);
  const [configurationDraft, setConfigurationDraft] = useState(null);
  const [testOpen, setTestOpen] = useState(false);
  const [testDraft, setTestDraft] = useState(null);
  const [pendingTest, setPendingTest] = useState(null);
  const [activeTool, setActiveTool] = useState(null);
  const [notificationWizardOpen, setNotificationWizardOpen] = useState(false);
  const [notificationDraft, setNotificationDraft] = useState(null);
  const [notificationRequestMetadata, setNotificationRequestMetadata] = useState(null);
  const [notificationOperation, setNotificationOperation] = useState(null);
  const [pendingAIDraft, setPendingAIDraft] = useState(null);
  const [communicationAISession, setCommunicationAISession] = useState(null);
  const [communicationAISessionLoading, setCommunicationAISessionLoading] = useState(false);

  const canLoadTool = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.loadClientCommunicationTool,
  );
  const canSelectProviders = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.selectClientCommunicationProviders,
  );
  const canSaveConfiguration = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.saveClientCommunicationConfiguration,
  );
  const canValidateProvider = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.validateClientCommunicationProvider,
  );
  const canTestProvider = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.testClientCommunicationProvider,
  );
  const canSaveToolItem = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.saveClientCommunicationToolItem,
  );
  const canRemoveToolItem = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.removeClientCommunicationToolItem,
  );
  const canSearchRecipients = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.searchClientNotificationRecipients,
  );
  const canSaveNotificationDraft = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.saveClientInternalNotificationDraft,
  );
  const canDispatchNotification = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.dispatchClientInternalNotification,
  );
  const canUseAI = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.aiQuestionClientCommunicationManage,
  );

  workspaceRef.current = workspaceManager;
  configurationRef.current = configurationManager;
  providerActionRef.current = providerActionManager;
  notificationRequestRef.current = notificationRequestManager;
  aiChatSessionRef.current = aiChatSessionManager;

  const closeNotificationStream = useCallback(() => {
    notificationStreamRef.current?.close?.();
    notificationStreamRef.current = null;
  }, []);

  useEffect(() => () => {
    closeNotificationStream();
  }, [closeNotificationStream]);

  const relinkCommunicationAISession = async (
    sourceSessionId,
    referenceId,
    referenceName,
  ) => {
    const normalizedSessionId = String(sourceSessionId || '').trim();
    const normalizedReferenceId = String(referenceId || '').trim();
    if (!normalizedSessionId || !normalizedReferenceId) return null;

    let sourceSession = String(communicationAISession?._id || '')
      === normalizedSessionId
      ? communicationAISession
      : null;
    if (!sourceSession && typeof aiChatSessionRef.current?.getItem === 'function') {
      const loaded = await aiChatSessionRef.current.getItem(normalizedSessionId);
      sourceSession = getFirstResultItem(loaded);
    }
    if (!sourceSession?._id) {
      throw new Error('The active AI conversation could not be loaded for this notification draft.');
    }

    if (
      String(sourceSession.ReferenceId || '') === normalizedReferenceId
      && String(sourceSession.ReferenceItemName || '') === String(referenceName)
    ) {
      setCommunicationAISession(sourceSession);
      return sourceSession;
    }

    const migratedSession = {
      ...sourceSession,
      ReferenceId: normalizedReferenceId,
      ReferenceItemName: referenceName,
    };
    const saved = await aiChatSessionRef.current.saveItem(migratedSession);
    if (saved?.Success === false) {
      throw new Error(
        saved?.DisplayMessage
        || 'The AI conversation could not be linked to this notification draft.',
      );
    }
    const persistedSession = getFirstResultItem(saved) || migratedSession;
    setCommunicationAISession(persistedSession);
    return persistedSession;
  };

  const request = useCallback(
    (overrides = {}) => createCommunicationRequest({
      workspace,
      clientId,
      appId,
      currentEnvironment,
      overrides,
    }),
    [appId, clientId, currentEnvironment, workspace],
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const [workspaceResult, configurationResult, providerResult] = await Promise.all([
        runOperation(
          workspaceRef.current,
          CLIENT_COMMUNICATION_OPERATIONS.loadWorkspace,
          createCommunicationRequest({
            workspace: null,
            clientId,
            appId,
            currentEnvironment,
          }),
          'Client Communications is unavailable.',
        ),
        configurationRef.current.GetMetadata({ forceReload: true }),
        providerActionRef.current.GetMetadata({ forceReload: true }),
      ]);
      setWorkspace(workspaceResult);
      setConfigurationMetadata(
        getFirstMetadata(configurationResult, configurationRef.current),
      );
      setProviderActionMetadata(
        getFirstMetadata(providerResult, providerActionRef.current),
      );
    } catch (ex) {
      setWorkspace(null);
      setError(ex?.message || 'Client Communications is unavailable.');
    } finally {
      setLoading(false);
    }
  }, [appId, clientId, currentEnvironment]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const configurationFields = useMemo(
    () => selectCommunicationFields(
      configurationMetadata,
      CLIENT_COMMUNICATION_CONFIGURATION_FIELDS,
    ),
    [configurationMetadata],
  );
  const selectedConfigurationFields = useMemo(() => {
    const selectedSettingKeys = new Set(
      (workspace?.Providers || [])
        .filter((provider) => provider.IsSelected)
        .flatMap((provider) => provider.SettingKeys || [])
        .flatMap((key) => [key, `${key}Input`])
        .map((key) => String(key).toLowerCase()),
    );
    return configurationFields.filter((field) => selectedSettingKeys.has(
      String(field?.FieldName || field?.fieldName || '').toLowerCase(),
    ));
  }, [configurationFields, workspace?.Providers]);
  const providerTestFields = useMemo(
    () => selectCommunicationFields(
      providerActionMetadata,
      CLIENT_COMMUNICATION_TEST_FIELDS,
    ),
    [providerActionMetadata],
  );

  const execute = async (
    operationName,
    operationRequest,
    successMessage,
    close = null,
    errorSurface = 'page',
    resultTitle = '',
  ) => {
    setBusy(true);
    setError('');
    setMessage('');
    setOperationResult(null);
    try {
      const updated = await runOperation(
        workspaceRef.current,
        operationName,
        operationRequest,
        successMessage,
      );
      setWorkspace(updated);
      if (close) close();
      if (errorSurface === 'result-dialog') {
        setOperationResult({
          title: `${resultTitle} Result`,
          message: updated?.StatusMessage || successMessage,
          tone: 'success',
        });
      } else {
        setMessage(updated?.StatusMessage || successMessage);
      }
      return updated;
    } catch (ex) {
      const failureMessage = ex?.message || successMessage;
      if (errorSurface === 'page') {
        setError(failureMessage);
      } else if (errorSurface === 'result-dialog') {
        setOperationResult({
          title: `${resultTitle} Result`,
          message: failureMessage,
          tone: 'error',
        });
        return null;
      }
      throw ex;
    } finally {
      setBusy(false);
    }
  };

  const openConfiguration = () => {
    setConfigurationDraft(
      cloneCommunicationValue(workspace?.Configuration || {}),
    );
    setConfigurationOpen(true);
  };

  const openProviderSelection = () => {
    setSelectedProviderKeys(
      (workspace?.Providers || [])
        .filter((provider) => provider.IsSelected)
        .map((provider) => provider.Key),
    );
    setProviderSelectionOpen(true);
  };

  const saveProviderSelection = async () => execute(
    CLIENT_COMMUNICATION_OPERATIONS.selectProviders,
    request({
      SelectedProviderKeys: selectedProviderKeys,
      ExpectedProviderSelectionVersion:
        workspace?.ExpectedProviderSelectionVersion || '',
    }),
    'Communication provider selections saved.',
    () => setProviderSelectionOpen(false),
    'dialog',
  );

  const saveConfiguration = async (updated) => {
    const savedWorkspace = await execute(
      CLIENT_COMMUNICATION_OPERATIONS.saveConfiguration,
      request({ Configuration: updated || configurationDraft }),
      'Communication provider configuration saved.',
      () => setConfigurationOpen(false),
    );
    return {
      Success: true,
      Items: [savedWorkspace?.Configuration || updated],
    };
  };

  const validateProvider = async (provider) => execute(
    CLIENT_COMMUNICATION_OPERATIONS.validateProvider,
    request({
      ProviderAction: {
        ProviderKey: provider.Key,
      },
    }),
    'Provider validation completed.',
    null,
    'result-dialog',
    `${provider.DisplayName} Validation`,
  );

  const openTest = (provider) => {
    setTestDraft({
      ...(workspace?.ProviderAction || {}),
      ProviderKey: provider.Key,
      TestRecipient: '',
      TestSubject: '',
      TestMessage: '',
      ConfirmTestSend: false,
    });
    setTestOpen(true);
  };

  const stageTest = async (updated) => {
    setTestOpen(false);
    setPendingTest({
      ...updated,
      ProviderKey: testDraft.ProviderKey,
      ConfirmTestSend: true,
    });
    return { Success: true, Items: [updated] };
  };

  const sendTest = async () => execute(
    CLIENT_COMMUNICATION_OPERATIONS.testProvider,
    request({ ProviderAction: pendingTest }),
    'The provider test completed.',
    () => setPendingTest(null),
    'dialog',
  );

  const openTool = async (tool) => {
    if (!canLoadTool) return;
    setBusy(true);
    setError('');
    setMessage('');
    setActiveTool(tool);
    try {
      const loaded = await runOperation(
        workspaceRef.current,
        CLIENT_COMMUNICATION_OPERATIONS.loadTool,
        request({
          ActiveToolKey: tool.Key,
          PageNumber: 1,
          SearchText: '',
        }),
        `${tool.DisplayName} is unavailable.`,
      );
      setWorkspace(loaded);
    } catch (ex) {
      setActiveTool(null);
      setError(ex?.message || `${tool.DisplayName} is unavailable.`);
    } finally {
      setBusy(false);
    }
  };

  const loadToolPage = async (pageNumber, searchText) => execute(
    CLIENT_COMMUNICATION_OPERATIONS.loadTool,
    request({
      ActiveToolKey: activeTool.Key,
      PageNumber: pageNumber,
      SearchText: searchText,
    }),
    `${activeTool.DisplayName} refreshed.`,
  );

  const applyNotificationFilter = async (filterValue) => {
    const normalized = normalizeNotificationFilter(filterValue);
    setBusy(true);
    try {
      const loaded = await runOperation(
        workspaceRef.current,
        CLIENT_COMMUNICATION_OPERATIONS.loadTool,
        request({
          ActiveToolKey: 'notification-requests',
          PageNumber: 1,
          SearchText: normalized.SearchText,
          NotificationFilter: normalized,
        }),
        'Notification Requests could not be filtered.',
      );
      setWorkspace(loaded);
      return loaded;
    } finally {
      setBusy(false);
    }
  };

  const applyNotificationLogFilter = async (filterValue) => {
    const normalized = normalizeNotificationLogFilter(filterValue);
    setBusy(true);
    try {
      const loaded = await runOperation(
        workspaceRef.current,
        CLIENT_COMMUNICATION_OPERATIONS.loadTool,
        request({
          ActiveToolKey: 'notifications-log',
          PageNumber: 1,
          SearchText: normalized.SearchText,
          NotificationLogFilter: normalized,
        }),
        'Notifications Log could not be filtered.',
      );
      setWorkspace(loaded);
      return loaded;
    } finally {
      setBusy(false);
    }
  };

  const loadNotificationRecipients = async () => {
    const loaded = await runOperation(
      workspaceRef.current,
      CLIENT_COMMUNICATION_OPERATIONS.searchNotificationRecipients,
      request({ ActiveToolKey: 'notification-requests', SearchText: '' }),
      'Active notification recipients are unavailable.',
    );
    setWorkspace((current) => ({
      ...current,
      NotificationRecipients: loaded?.NotificationRecipients || [],
    }));
    return loaded?.NotificationRecipients || [];
  };

  const loadNotificationOperation = async (requestId, eventId) => {
    setBusy(true);
    try {
      const loaded = await runOperation(
        workspaceRef.current,
        CLIENT_COMMUNICATION_OPERATIONS.loadCommunicationOperationStatus,
        request({
          ActiveToolKey: 'notification-requests',
          NotificationOperation: {
            RequestId: requestId,
            EventId: eventId,
          },
        }),
        'The selected delivery attempt could not be loaded.',
      );
      if (!loaded?.NotificationOperation?.EventId) {
        throw new Error('The selected delivery attempt did not return status details.');
      }
      setNotificationOperation(loaded.NotificationOperation);
      setWorkspace((current) => ({
        ...current,
        OperationCorrelationId: loaded.OperationCorrelationId,
        NotificationOperation: loaded.NotificationOperation,
      }));
      return {
        Success: true,
        Items: [loaded],
        Operation: loaded.NotificationOperation,
      };
    } catch (ex) {
      return {
        Success: false,
        DisplayMessage: ex?.message || 'The selected delivery attempt could not be loaded.',
      };
    } finally {
      setBusy(false);
    }
  };

  const connectNotificationStream = async (requestId, totalCount) => {
    closeNotificationStream();
    if (typeof notificationRequestRef.current?.GetStream !== 'function') {
      return {
        Success: false,
        DisplayMessage: 'Live delivery updates are unavailable for this notification.',
      };
    }
    const streamController = await notificationRequestRef.current.GetStream({
      operationName: 'ProfileNotificationCreate',
      id: requestId,
      type: 'sse',
    });
    if (!streamController?.Success || typeof streamController.onMessage !== 'function') {
      return {
        Success: false,
        DisplayMessage: streamController?.DisplayMessage
          || 'Live delivery updates could not be started.',
      };
    }
    notificationStreamRef.current = streamController;
    streamController.onMessage((streamMessage) => {
      if (notificationStreamRef.current !== streamController) return;
      setNotificationOperation((current) => (
        current?.IsCurrentEvent === false
          ? current
          : mergeNotificationStreamMessage(
            current,
            streamMessage,
            requestId,
            totalCount,
          )
      ));
    });
    void streamController.untilDone
      .catch(() => {
        if (notificationStreamRef.current !== streamController) return;
        setNotificationOperation((current) => (
          current?.IsCurrentEvent === false
            ? current
            : {
              ...(current || {}),
              RequestId: current?.RequestId || requestId,
              StreamWarning: 'Live delivery updates stopped. Reopen this notification to load its latest saved status, or use Event Manager for full operation details.',
            }
        ));
      })
      .finally(() => {
        if (notificationStreamRef.current === streamController) {
          notificationStreamRef.current = null;
        }
      });
    return { Success: true, Controller: streamController };
  };

  const openNotificationWizard = async (
    record = null,
    draftOverrides = null,
    sourceAISessionId = null,
  ) => {
    if (!canSearchRecipients) return;
    setBusy(true);
    try {
      const [notificationRecipients, metadataResult] = await Promise.all([
        loadNotificationRecipients(),
        notificationRequestRef.current.GetMetadata({}),
      ]);
      const loadedMetadata = getFirstMetadata(
        metadataResult,
        notificationRequestRef.current,
      );
      const metadataFields = loadedMetadata?.Fields || loadedMetadata?.fields || [];
      const availableFieldNames = new Set(metadataFields.map((field) => (
        String(field?.FieldName || field?.fieldName || '').toLowerCase()
      )));
      const missingFields = INTERNAL_NOTIFICATION_ITEM_FIELD_NAMES.filter(
        (fieldName) => !availableFieldNames.has(fieldName.toLowerCase()),
      );
      if (!loadedMetadata || missingFields.length) {
        throw new Error(
          `Notification Request metadata is missing: ${missingFields.join(', ') || 'item metadata'}.`,
        );
      }
      setNotificationRequestMetadata(loadedMetadata);
      const now = Date.now();
      const start = record?.StartDate ? new Date(record.StartDate).getTime() : 0;
      const status = String(record?.DeliveryStatus || '').toLowerCase();
      const requestId = record ? communicationRecordId(record) : null;
      const action = !record
        ? 'Create'
        : status === 'draft'
          ? 'Create'
        : status === 'failed'
          ? 'Retry'
          : record.IsActive !== false && start > now
            ? 'UpdateScheduled'
            : record.IsActive !== false
              ? 'AddRecipients'
              : 'Resend';
      const resolvedDraftOverrides = applyNotificationAIRecipientSelection(
        draftOverrides || {},
        notificationRecipients,
      );
      const nextDraft = createInternalNotificationDraft({
        ...(record ? {
          RequestId: requestId,
          SourceRequestId: status === 'draft' ? null : requestId,
          Action: action,
          RecipientProfileIds: record.SendToProfileIds || [],
          NotificationType: Number(record.NotificationType || 0),
          Title: record.Title || '',
          Message: record.Message || '',
          SendNow: start <= now,
          StartDate: record.StartDate || null,
          EndDate: record.EndDate || null,
        } : {}),
        ...resolvedDraftOverrides,
      });
      if (!record && sourceAISessionId) {
        await relinkCommunicationAISession(
          sourceAISessionId,
          nextDraft.SubmissionKey,
          NOTIFICATION_DRAFT_AI_REFERENCE_NAME,
        );
        notificationDraftSourceAISessionIdRef.current = sourceAISessionId;
      } else {
        notificationDraftSourceAISessionIdRef.current = null;
      }
      setNotificationDraft(nextDraft);
      closeNotificationStream();
      setNotificationOperation(null);
      const savedDeliveryEventId = record?.DeliveryEventId
        || record?.DeliveryCorrelationId
        || undefined;
      if (record && (status !== 'draft' || savedDeliveryEventId)) {
        const loadedResult = await loadNotificationOperation(
          requestId,
          savedDeliveryEventId,
        );
        if (loadedResult?.Success === false) {
          throw new Error(
            loadedResult.DisplayMessage
            || 'The saved notification delivery status could not be loaded.',
          );
        }
        const loadedOperation = loadedResult.Operation;
        const currentAttempt = (loadedOperation?.EventHistory || [])
          .find((entry) => entry.IsCurrent) || null;
        if (loadedOperation?.IsCurrentEvent !== false
          && currentAttempt?.IsTerminal !== true
          && loadedOperation?.IsTerminal !== true) {
          const streamResult = await connectNotificationStream(
            requestId,
            loadedOperation?.TotalCount || nextDraft.RecipientProfileIds?.length || 0,
          );
          if (streamResult?.Success === false) {
            setNotificationOperation((current) => ({
              ...(current || loadedOperation),
              StreamWarning: `${streamResult.DisplayMessage} The latest saved status is shown below.`,
            }));
          }
        }
      }
      setNotificationWizardOpen(true);
    } catch (ex) {
      setOperationResult({
        title: record ? 'Manage Internal Notification' : 'Add Notification',
        message: ex?.message || 'Active notification recipients are unavailable.',
        tone: 'error',
      });
    } finally {
      setBusy(false);
    }
  };

  const saveInternalNotificationDraft = async (
    draft,
    {
      updateState = true,
      timeoutMessage = 'Saving the notification draft took too long. Nothing was sent; try Next again.',
    } = {},
  ) => {
    setBusy(true);
    try {
      const saved = await withOperationTimeout(
        runOperation(
          workspaceRef.current,
          CLIENT_COMMUNICATION_OPERATIONS.saveInternalNotificationDraft,
          request({
            ActiveToolKey: 'notification-requests',
            NotificationDraft: draft,
          }),
          'The notification draft was not saved.',
        ),
        timeoutMessage,
      );
      if (!saved?.NotificationDraft?.RequestId) {
        throw new Error('The notification draft was not returned after it was saved.');
      }
      const persistedDraft = saved.NotificationDraft;
      if (updateState) {
        const sourceAISessionId = notificationDraftSourceAISessionIdRef.current;
        if (sourceAISessionId) {
          await relinkCommunicationAISession(
            sourceAISessionId,
            persistedDraft.RequestId,
            NOTIFICATION_REQUEST_AI_REFERENCE_NAME,
          );
          notificationDraftSourceAISessionIdRef.current = null;
        }
        setNotificationDraft(persistedDraft);
        setWorkspace((current) => ({
          ...current,
          NotificationDraft: persistedDraft,
        }));
      }
      return { Success: true, Draft: persistedDraft };
    } catch (ex) {
      return {
        Success: false,
        DisplayMessage: ex?.message || 'The notification draft was not saved.',
      };
    } finally {
      setBusy(false);
    }
  };

  const submitInternalNotification = async (draft) => {
    setBusy(true);
    let streamController = null;
    try {
      if (!draft?.RequestId) {
        throw new Error('Save the notification draft before sending it.');
      }
      const streamResult = await connectNotificationStream(
        draft.RequestId,
        draft.RecipientProfileIds?.length || 0,
      );
      streamController = streamResult?.Controller || null;
      if (streamResult?.Success === false) {
        throw new Error(
          streamResult.DisplayMessage
          || 'Live delivery updates could not be started. Nothing was sent; try again.',
        );
      }
      const accepted = await withOperationTimeout(
        runOperation(
          workspaceRef.current,
          CLIENT_COMMUNICATION_OPERATIONS.dispatchInternalNotification,
          request({
            ActiveToolKey: 'notification-requests',
            NotificationDraft: draft,
          }),
          'The internal notification was not accepted.',
        ),
        'Sending took too long to return a status. The saved draft was retained; refresh Notification Requests before trying again.',
      );
      if (!accepted?.NotificationOperation?.EventId) {
        throw new Error('The send operation did not return an event to track. The saved draft was retained.');
      }
      setNotificationOperation((current) => ({
        ...accepted.NotificationOperation,
        ...(current?.EventId === accepted.NotificationOperation.EventId ? current : {}),
        EventId: accepted.NotificationOperation.EventId,
        IsCurrentEvent: true,
      }));
      setWorkspace((current) => ({
        ...current,
        OperationCorrelationId: accepted.OperationCorrelationId,
        NotificationOperation: accepted.NotificationOperation,
      }));
      await loadNotificationOperation(
        accepted.NotificationOperation.RequestId,
        accepted.NotificationOperation.EventId,
      );
      return { Success: true, Items: [accepted] };
    } catch (ex) {
      if (notificationStreamRef.current === streamController) {
        closeNotificationStream();
      }
      return {
        Success: false,
        DisplayMessage: ex?.message || 'The internal notification was not accepted.',
      };
    } finally {
      setBusy(false);
    }
  };

  const refreshNotificationRequestsAfterTerminal = useCallback(async () => {
    if (activeTool?.Key !== 'notification-requests') return;
    try {
      const loaded = await runOperation(
        workspaceRef.current,
        CLIENT_COMMUNICATION_OPERATIONS.loadTool,
        request({
          ActiveToolKey: 'notification-requests',
          PageNumber: 1,
        }),
        'Notification Requests could not be refreshed.',
      );
      setWorkspace((current) => ({
        ...loaded,
        NotificationRecipients: current?.NotificationRecipients || [],
      }));
    } catch (ex) {
      setOperationResult({
        title: 'Notification Requests Refresh',
        message: ex?.message || 'Notification Requests could not be refreshed.',
        tone: 'error',
      });
    }
  }, [activeTool?.Key, request]);

  const notificationDraftHasOperatorChanges = () => {
    const isAIPreparedPlaceholder = notificationDraft?.RequestId
      && String(notificationDraft.RequestId)
        === String(aiPreparedNotificationRequestIdRef.current || '');
    const title = String(notificationDraft?.Title || '').trim();
    const messageValue = String(notificationDraft?.Message || '').trim();
    return Boolean(
      (title && (!isAIPreparedPlaceholder || title !== NEW_NOTIFICATION_AI_DRAFT_TITLE))
      || (messageValue && (
        !isAIPreparedPlaceholder
        || messageValue !== NEW_NOTIFICATION_AI_DRAFT_MESSAGE
      ))
      || notificationDraft?.RecipientProfileIds?.length
      || notificationDraft?.StartDate
      || notificationDraft?.EndDate,
    );
  };

  const applyAIDraft = async (value, sourceAISessionId = null) => {
    const normalized = normalizeNotificationAIDraft(value);
    if (notificationOperation && !notificationOperation.IsTerminal) return;
    if (!notificationWizardOpen) {
      await openNotificationWizard(null, normalized, sourceAISessionId);
      return;
    }
    const resolved = applyNotificationAIRecipientSelection(
      normalized,
      workspace?.NotificationRecipients || [],
    );
    if (notificationWizardOpen && notificationDraftHasOperatorChanges()) {
      setPendingAIDraft(resolved);
      return;
    }
    aiPreparedNotificationRequestIdRef.current = null;
    setNotificationDraft((current) => ({ ...current, ...resolved }));
  };

  const handleCommunicationAIUpdate = async (uiAction, item, value) => {
    const action = String(uiAction || '').trim().toLowerCase();
    if (activeTool?.Key === 'notification-requests') {
      if (action === 'filter') await applyNotificationFilter(value || {});
      if (action === 'update') {
        const sourceAISessionId = item?.ChatSessionId
          || item?.chatSessionId
          || communicationAISession?._id
          || null;
        await applyAIDraft(value || {}, sourceAISessionId);
      }
      return;
    }
    if (activeTool?.Key === 'notifications-log' && action === 'filter') {
      await applyNotificationLogFilter(value || {});
    }
  };

  const saveToolItem = async (updated, original) => execute(
    CLIENT_COMMUNICATION_OPERATIONS.saveToolItem,
    request({
      ActiveToolKey: activeTool.Key,
      RecordJson: JSON.stringify(updated),
      RecordId: original ? communicationRecordId(original) : '',
      ExpectedRecordVersion: original ? communicationRecordVersion(original) : '',
    }),
    `${activeTool.DisplayName} record saved.`,
  );

  const removeToolItem = async (record) => execute(
    CLIENT_COMMUNICATION_OPERATIONS.removeToolItem,
    request({
      ActiveToolKey: activeTool.Key,
      RecordId: communicationRecordId(record),
      ExpectedRecordVersion: communicationRecordVersion(record),
    }),
    `${activeTool.DisplayName} record removed.`,
  );

  const toolAIReferenceId = activeTool?.Key
    ? `${clientId}:${appId}:${activeTool.Key}`
    : undefined;
  const notificationRequestAIReferenceId = notificationWizardOpen
    ? String(notificationDraft?.RequestId || notificationOperation?.RequestId || '').trim()
    : '';
  const notificationDraftAIReferenceId = notificationWizardOpen
    ? String(notificationDraft?.SubmissionKey || '').trim()
    : '';
  const communicationAIReferenceId = notificationRequestAIReferenceId
    || notificationDraftAIReferenceId
    || toolAIReferenceId;
  const communicationAIReferenceName = notificationRequestAIReferenceId
    ? NOTIFICATION_REQUEST_AI_REFERENCE_NAME
    : notificationDraftAIReferenceId
      ? NOTIFICATION_DRAFT_AI_REFERENCE_NAME
      : COMMUNICATION_AI_REFERENCE_NAME;
  const communicationAIPanelKey = toolAIReferenceId || 'communications';
  const selectedDraft = notificationWizardOpen && notificationDraft
    ? notificationDraft
    : activeTool
      ? (workspace?.Records || [])[0] || workspace?.NewRecordTemplate || {}
      : {};
  const aiPayload = {
    Draft: activeTool?.Key === 'notifications-log'
      ? undefined
      : activeTool?.Key === 'notification-requests'
        ? buildSafeNotificationDraftAIPayload(selectedDraft)
        : buildSafeCommunicationAIPayload(selectedDraft),
    Filter: activeTool?.Key === 'notification-requests'
      ? workspace?.NotificationFilter || EMPTY_NOTIFICATION_FILTER
      : activeTool?.Key === 'notifications-log'
        ? workspace?.NotificationLogFilter || EMPTY_NOTIFICATION_LOG_FILTER
        : undefined,
    NotificationDiagnostics: activeTool?.Key === 'notifications-log'
      ? buildSafeNotificationLogDiagnostics(workspace?.Records)
      : undefined,
    TotalRecords: activeTool?.Key === 'notifications-log'
      ? Number(workspace?.TotalRecords || 0)
      : undefined,
    ProviderReadiness: (workspace?.Providers || []).map((provider) => ({
      Key: provider.Key,
      Channel: provider.Channel,
      Status: provider.Status,
      IsSelected: !!provider.IsSelected,
    })),
  };

  const prepareCommunicationAISend = async (context = {}) => {
    if (
      activeTool?.Key !== 'notification-requests'
      || !notificationWizardOpen
      || notificationRequestAIReferenceId
    ) {
      return null;
    }

    const currentDraft = notificationDraft || {};
    const draftToPersist = {
      ...currentDraft,
      Title: String(currentDraft.Title || '').trim()
        || NEW_NOTIFICATION_AI_DRAFT_TITLE,
      Message: String(currentDraft.Message || '').trim()
        || NEW_NOTIFICATION_AI_DRAFT_MESSAGE,
    };
    const result = await saveInternalNotificationDraft(draftToPersist, {
      updateState: false,
      timeoutMessage: 'Preparing the notification draft took too long. Nothing was sent; try the AI message again.',
    });
    if (result?.Success === false || !result?.Draft?.RequestId) {
      return {
        Success: false,
        DisplayMessage: result?.DisplayMessage
          || 'The notification draft could not be prepared for AI assistance.',
      };
    }

    const persistedDraft = result.Draft;
    preparedNotificationDraftRef.current = persistedDraft;
    aiPreparedNotificationRequestIdRef.current = persistedDraft.RequestId;
    return {
      referenceId: String(persistedDraft.RequestId),
      referenceName: NOTIFICATION_REQUEST_AI_REFERENCE_NAME,
      params: {
        ...(context.params || {}),
        notificationRequestId: String(persistedDraft.RequestId),
        notificationDraftKey: persistedDraft.SubmissionKey || undefined,
      },
      payload: {
        ...(context.payload || {}),
        Draft: buildSafeNotificationDraftAIPayload(persistedDraft),
      },
    };
  };

  const handleCommunicationAISessionChange = (_sessionId, sessionItem) => {
    setCommunicationAISession(sessionItem || null);
    const preparedDraft = preparedNotificationDraftRef.current;
    if (
      !preparedDraft?.RequestId
      || String(sessionItem?.ReferenceId || '') !== String(preparedDraft.RequestId)
      || String(sessionItem?.ReferenceItemName || '')
        !== NOTIFICATION_REQUEST_AI_REFERENCE_NAME
    ) {
      return;
    }

    preparedNotificationDraftRef.current = null;
    setNotificationDraft(preparedDraft);
    setWorkspace((current) => ({
      ...current,
      NotificationDraft: preparedDraft,
    }));
  };

  useEffect(() => {
    let cancelled = false;

    const querySessions = async (referenceId, referenceName) => {
      if (!referenceId || !aiChatSessionRef.current?.Query) return [];
      const result = await aiChatSessionRef.current.Query({
        model: 'x => x.IsActive == true && x.IsDeleted == false && x.ReferenceId == referenceId',
        params: { referenceId },
      });
      return getResultItems(result)
        .filter((item) => (
          String(item?.ReferenceId || '') === String(referenceId)
          && String(item?.ReferenceItemName || '') === String(referenceName)
          && (!item?.ClientId || String(item.ClientId) === String(clientId))
          && (!item?.AppId || String(item.AppId) === String(appId))
        ))
        .sort((left, right) => (
          new Date(right?.UpdatedDate || right?.CreatedDate || 0).getTime()
          - new Date(left?.UpdatedDate || left?.CreatedDate || 0).getTime()
        ));
    };

    const restoreSession = async () => {
      if (!activeTool || !communicationAIReferenceId) {
        setCommunicationAISession(null);
        setCommunicationAISessionLoading(false);
        return;
      }

      if (
        communicationAISession?._id
        && String(communicationAISession.ReferenceId || '')
          === String(communicationAIReferenceId)
        && String(communicationAISession.ReferenceItemName || '')
          === String(communicationAIReferenceName)
      ) {
        setCommunicationAISessionLoading(false);
        return;
      }

      setCommunicationAISession(null);
      setCommunicationAISessionLoading(true);
      try {
        const matching = await querySessions(
          communicationAIReferenceId,
          communicationAIReferenceName,
        );
        let selected = matching[0] || null;

        if (!selected && communicationAIReferenceName === NOTIFICATION_REQUEST_AI_REFERENCE_NAME) {
          const fallbackReferences = [
            notificationDraftAIReferenceId && notificationDraftAIReferenceId !== communicationAIReferenceId
              ? {
                ReferenceId: notificationDraftAIReferenceId,
                ReferenceItemName: NOTIFICATION_DRAFT_AI_REFERENCE_NAME,
              }
              : null,
            toolAIReferenceId
              ? {
                ReferenceId: toolAIReferenceId,
                ReferenceItemName: COMMUNICATION_AI_REFERENCE_NAME,
              }
              : null,
          ].filter(Boolean);

          for (const fallback of fallbackReferences) {
            const legacy = await querySessions(
              fallback.ReferenceId,
              fallback.ReferenceItemName,
            );
            if (legacy.length !== 1) continue;
            const migrated = {
              ...legacy[0],
              ReferenceId: communicationAIReferenceId,
              ReferenceItemName: communicationAIReferenceName,
            };
            const saveResult = await aiChatSessionRef.current.saveItem(migrated);
            selected = getFirstResultItem(saveResult) || migrated;
            break;
          }
        }

        if (!cancelled) setCommunicationAISession(selected);
      } catch {
        if (!cancelled) setCommunicationAISession(null);
      } finally {
        if (!cancelled) setCommunicationAISessionLoading(false);
      }
    };

    restoreSession();
    return () => {
      cancelled = true;
    };
  }, [
    activeTool,
    communicationAIReferenceId,
    communicationAIReferenceName,
    appId,
    clientId,
    notificationDraftAIReferenceId,
    toolAIReferenceId,
    communicationAISession?._id,
    communicationAISession?.ReferenceId,
    communicationAISession?.ReferenceItemName,
  ]);
  const commandBarActions = [
    {
      key: 'refresh-client-communications',
      label: 'Refresh',
      iconKey: 'refresh',
      disabled: loading || busy,
      onClick: refresh,
    },
    ...(canSelectProviders && workspace?.SupportsProviderConfiguration ? [{
      key: 'select-client-communication-providers',
      label: 'Select Providers',
      iconKey: 'checklist',
      disabled: busy,
      onClick: openProviderSelection,
    }] : []),
    ...(canSaveConfiguration && workspace?.SupportsProviderConfiguration ? [{
      key: 'configure-client-communication-providers',
      label: 'Configure Providers',
      iconKey: 'settings',
      disabled: !configurationMetadata
        || selectedConfigurationFields.length === 0
        || busy,
      onClick: openConfiguration,
    }] : []),
  ];

  return (
    <div className="resource-workspace-overview command-entry command-communications">
      <ConvokeCommandBar
        actions={commandBarActions}
        ariaLabel="Communications actions"
        className="convoke-command-bar--screen"
        useSparkButtons
      />

      <CommunicationsState
        loading={loading}
        error={error}
        message={message}
      />
      {!loading && workspace ? (
        <>
          {!workspace.SupportsProviderConfiguration ? (
            <ResourceStateControl
              compact
              title="Provider configuration unavailable"
              message="Compiled provider settings are currently defined only for the Convoke application. The authorized item tools remain available for this application scope."
              role="status"
            />
          ) : null}
          <div className="resource-workspace-two-column">
            <ProviderCards
              providers={workspace.Providers}
              busy={busy}
              canValidate={canValidateProvider}
              canTest={canTestProvider}
              onValidate={validateProvider}
              onTest={openTest}
            />
            <ToolCards
              tools={workspace.Tools}
              providers={workspace.Providers}
              busy={busy}
              canLoad={canLoadTool}
              onOpen={openTool}
            />
          </div>
        </>
      ) : null}

      <DialogControl
        openDialog={providerSelectionOpen}
        title="Select Providers"
        subTitle="Select the communication providers available to this client. Configure Providers manages settings for the selected external providers."
        okText="Save Providers"
        cancelText="Cancel"
        onCancel={() => setProviderSelectionOpen(false)}
        onX={() => setProviderSelectionOpen(false)}
        onOk={saveProviderSelection}
        disableOk={busy}
        loadingOk={busy}
        className="command-entry__options-dialog"
      >
        <CardGridControl
          className="command-entry__options-card-grid"
          rows={workspace?.Providers || []}
          metadata={CLIENT_COMMUNICATION_PROVIDER_CARD_METADATA}
          cardFields={CLIENT_COMMUNICATION_PROVIDER_CARD_METADATA.Fields}
          rowKey={(provider) => provider.Key}
          selectedRowKeys={selectedProviderKeys}
          onSelectedRowKeysChange={setSelectedProviderKeys}
          selectable
          multiSelect
          activeFilterEnabled={false}
          emptyContent="No communication providers are available."
          minCardWidth="16rem"
          maxColumns={Math.min(
            2,
            Math.max(1, workspace?.Providers?.length || 1),
          )}
        />
      </DialogControl>

      <SidePanelFormControl
        open={configurationOpen}
        title="Communication Provider Configuration"
        metadata={configurationMetadata}
        formFields={selectedConfigurationFields}
        itemData={configurationDraft}
        onSave={saveConfiguration}
        onCancel={() => setConfigurationOpen(false)}
        onUpdateSidePanel={() => {}}
        saveButtonText={busy ? 'Saving...' : 'Save'}
        setOpenEditSidePanel={setConfigurationOpen}
        setItemData={setConfigurationDraft}
        viewOnly={false}
      />

      <SidePanelFormControl
        open={testOpen}
        title={`Test ${testDraft?.ProviderKey || 'communication provider'}`}
        metadata={providerActionMetadata}
        formFields={providerTestFields}
        itemData={testDraft}
        onSave={stageTest}
        onCancel={() => setTestOpen(false)}
        onUpdateSidePanel={() => {}}
        saveButtonText="Review Test"
        setOpenEditSidePanel={setTestOpen}
        setItemData={setTestDraft}
        viewOnly={false}
      />

      <AIPanel
        key={communicationAIPanelKey}
        open={!!activeTool}
        presentation="slideout"
        mapToClass="core-layout"
        title={`AI ${activeTool?.DisplayName || 'Communications'} Assistant`}
        onCancel={() => {
          if (notificationWizardOpen) {
            closeNotificationStream();
            setNotificationWizardOpen(false);
            if (notificationDraft?.RequestId) {
              refreshNotificationRequestsAfterTerminal();
            }
            if (notificationOperation?.IsSuccessful) {
              setNotificationDraft(null);
              setNotificationOperation(null);
            }
          }
          setActiveTool(null);
        }}
        onUpdatePanel={handleCommunicationAIUpdate}
        onBeforeSend={prepareCommunicationAISend}
        onSessionChange={handleCommunicationAISessionChange}
        session={communicationAISession}
        operationName={canUseAI
          ? CLIENT_COMMUNICATION_OPERATIONS.aiQuestionManage
          : undefined}
        chatEnabled={canUseAI}
        referenceId={communicationAIReferenceId}
        referenceName={communicationAIReferenceName}
        params={{
          clientId,
          applicationId: appId,
          currentEnvironment,
          toolKey: activeTool?.Key,
          notificationRequestId: notificationRequestAIReferenceId || undefined,
          notificationDraftKey: notificationDraftAIReferenceId || undefined,
        }}
        payload={aiPayload}
        chatPane="right"
        chatPaneCollapseMode="open"
        chatPaneStartOpen
        aiProcessingMode="Direct"
        singleSessionMode
        planningPreviewEnabled={false}
        previewPanelEnabled={false}
        contentReady={!communicationAISessionLoading}
        loadingContent={<Loading />}
        splashMessage={activeTool?.Key === 'notification-requests'
          ? 'Try: filter scheduled requests, find failures, or draft an internal notification.'
          : activeTool?.Key === 'notifications-log'
            ? 'Try: show unread notifications, find dismissed entries, or explain an unexpected notification state.'
            : `Ask AI to help with ${activeTool?.DisplayName || 'Communications'}.`}
        introMessage={activeTool?.Key === 'notification-requests'
          ? 'I can filter Notification Requests or prepare an internal-notification draft, including preselecting eligible users you explicitly name by email. You review the recipients and approve sending.'
          : activeTool?.Key === 'notifications-log'
            ? 'I can filter Notifications Log and help diagnose delivery and display state. I cannot change, resend, or delete notifications.'
            : 'AI can draft, review, and explain safe diagnostics. It cannot access credentials or recipients, save records, clear overrides, validate providers, send, or resend.'}
        minPanelWidth={980}
        minLeft={600}
        minRight={380}
      >
        {notificationWizardOpen ? (
          <ClientInternalNotificationWizard
            open={notificationWizardOpen}
            draft={notificationDraft}
            setDraft={setNotificationDraft}
            recipients={workspace?.NotificationRecipients || []}
            metadata={notificationRequestMetadata}
            operation={notificationOperation}
            busy={busy}
            onSaveDraft={saveInternalNotificationDraft}
            onSubmit={submitInternalNotification}
            canSaveDraft={canSaveNotificationDraft}
            canDispatch={canDispatchNotification}
            onSelectEvent={(eventId) => loadNotificationOperation(
              notificationOperation?.RequestId || notificationDraft?.RequestId,
              eventId,
            )}
            onTerminal={refreshNotificationRequestsAfterTerminal}
            onClose={() => {
              closeNotificationStream();
              setNotificationWizardOpen(false);
              if (notificationDraft?.RequestId) {
                refreshNotificationRequestsAfterTerminal();
              }
              if (notificationOperation?.IsSuccessful) {
                setNotificationDraft(null);
                setNotificationOperation(null);
              }
            }}
          />
        ) : activeTool ? (
          <CommunicationToolContent
            workspace={workspace}
            tool={activeTool}
            busy={busy}
            onLoadPage={loadToolPage}
            onSave={saveToolItem}
            onRemove={removeToolItem}
            onAddNotification={() => openNotificationWizard()}
            onManageNotification={(record) => openNotificationWizard(record)}
            onClearNotificationFilters={() => (
              activeTool?.Key === 'notifications-log'
                ? applyNotificationLogFilter({})
                : applyNotificationFilter({})
            )}
            canSaveItem={canSaveToolItem}
            canRemoveItem={canRemoveToolItem}
            canSaveNotificationDraft={canSaveNotificationDraft && canSearchRecipients}
          />
        ) : null}
      </AIPanel>

      <DialogControl
        openDialog={!!pendingAIDraft}
        title="Replace notification draft?"
        subTitle="The wizard contains operator changes. Review this confirmation before applying the AI draft update."
        okText="Replace Draft"
        cancelText="Keep Current Draft"
        onCancel={() => setPendingAIDraft(null)}
        onX={() => setPendingAIDraft(null)}
        onOk={() => {
          setNotificationDraft((current) => ({ ...current, ...pendingAIDraft }));
          setPendingAIDraft(null);
        }}
      >
        <p>Only explicitly requested, eligible email matches may replace the current recipient selection. Nothing will be sent.</p>
      </DialogControl>

      <DialogControl
        openDialog={!!operationResult}
        title={operationResult?.title || 'Communication Operation Result'}
        subTitle={operationResult?.tone === 'error'
          ? 'The operation did not complete.'
          : 'The operation completed.'}
        okText="Close"
        cancelText=""
        onCancel={() => setOperationResult(null)}
        onX={() => setOperationResult(null)}
        onOk={() => setOperationResult(null)}
      >
        <ConvokeDialogMessage
          message={operationResult?.message}
          tone={operationResult?.tone}
        />
      </DialogControl>

      <DialogControl
        openDialog={!!pendingTest}
        title="Send one provider test"
        subTitle="This operation records a test request and delivery log through the existing communication managers."
        okText="Confirm Test Send"
        cancelText="Cancel"
        onCancel={() => setPendingTest(null)}
        onX={() => setPendingTest(null)}
        onOk={sendTest}
        disableOk={busy}
        loadingOk={busy}
      >
        <p>
          Send one {pendingTest?.ProviderKey || 'provider'} test to the explicit
          recipient entered in the prior panel?
        </p>
      </DialogControl>
    </div>
  );
};

export default ClientCommunicationsScreen;
